import { Table } from 'ui/table';
import { CellAlignTypes } from 'modules/experiments/types';
import { TableCell } from 'ui/tableCell';
import styled from 'styled-components';
import { ColumnData, TableHeader } from 'ui/tableHeader';
import { colors } from 'ui/theme';
import { TableRows } from 'ui/tableRows';
import { TableHeaderCell } from './TableHeaderCell';
import { LocaleTableCell } from './LocaleTableCell';
import { ActionMenuTableCell } from './ActionMenuTableCell';
import { LanguageTableCell } from './LanguageTableCell';
import { memo } from 'react';
import {
  ModalState,
  ModalStatePayload,
  useProjectSettingsModalsContext,
} from 'modules/projectSettings/contexts';
import { MappedLocale } from 'modules/projectSettings/types';

export const StyledTableHeader = styled(TableHeader)`
border-color: transparent;
& > * {
    color: ${colors.textSecondary};
}
`;

export const StyledTableRows = styled(TableRows)`
:nth-child(even){
    background-color:  ${colors.backgroundPrimary};
}
`;

const RightAlignedTableCell = styled(TableCell)`
text-align: end;
`;

export interface ProjectLocales {
  locale: string;
  language: string | undefined;
  isDefault?: boolean;
  showModal: (modalState: ModalState<ModalStatePayload>) => void;
}

export type TableColumnData = ColumnData & {
  RowCell: ({ row }: { row: ProjectLocales }) => React.ReactElement;
};

const displayTableColumnText = {
  localeName: 'LOCALE',
  localeTooltip:
    "A user's location or a language preference, used by Iterable to select the right template version to send.",
  languageName: 'LANGUAGE',
  languageTooltip:
    'The language associated with a locale, used by Iterable to communicate with other platforms such as WhatsApp.',
};

export const LOCALE_COLUMN_CONFIGS: TableColumnData[] = [
  {
    value: 'locale',
    name: (
      <TableHeaderCell
        name={displayTableColumnText.localeName}
        tooltipContent={displayTableColumnText.localeTooltip}
      />
    ),
    sortable: false,
    align: CellAlignTypes.flexStart,
    RowCell: ({ row }) => (
      <TableCell>
        <LocaleTableCell row={row} />
      </TableCell>
    ),
  },
  {
    name: (
      <TableHeaderCell
        name={displayTableColumnText.languageName}
        tooltipContent={displayTableColumnText.languageTooltip}
      />
    ),
    value: 'language',
    sortable: false,
    align: CellAlignTypes.flexStart,
    RowCell: ({ row }) => (
      <TableCell>
        <LanguageTableCell row={row} />
      </TableCell>
    ),
  },
  {
    name: '',
    value: 'action',
    sortable: false,
    align: CellAlignTypes.flexEnd,
    RowCell: ({ row }) => (
      <RightAlignedTableCell>
        <ActionMenuTableCell row={row} />
      </RightAlignedTableCell>
    ),
  },
];

const MemoLocalesTable = memo(
  ({
    filteredItems,
    mappedLocales,
    defaultLocale,
    showModal,
  }: {
    filteredItems: any[];
    mappedLocales: MappedLocale[];
    defaultLocale: string;
    showModal: (modalState: ModalState<ModalStatePayload>) => void;
  }) => {
    return (
      <Table>
        <StyledTableHeader columns={LOCALE_COLUMN_CONFIGS} />
        <StyledTableRows
          idKey="locale"
          rows={filteredItems.map((item: string, index) => ({
            locale: item,
            language: mappedLocales.find((o) => o.tag === item)?.whatsAppInfobipLocale,
            isDefault: item === defaultLocale,
            showModal,
            /**
             * key is used to identify the row in the table.
             *
             * NOTE: There are cases where the same locale
             * is present multiple times in the filteredItems array, which causes unintended
             * side effects. That's why we're using the index as part of the key.
             */
            key: `row-${item}-${index}`,
          }))}
          columns={LOCALE_COLUMN_CONFIGS}
        />
      </Table>
    );
  },
);

export const LocalesTable = ({
  filteredItems,
  mappedLocales,
  defaultLocale,
}: { filteredItems: any[]; mappedLocales: MappedLocale[]; defaultLocale: string }) => {
  const { showModal } = useProjectSettingsModalsContext();

  return (
    <MemoLocalesTable
      filteredItems={filteredItems}
      mappedLocales={mappedLocales}
      defaultLocale={defaultLocale}
      showModal={showModal}
    />
  );
};
